/**
 * Section help — one short, plain explanation of what a section is and why it is there.
 *
 * ## Why this is not a hover tooltip
 *
 * "Hover instructions" is the natural way to ask for this, and hover alone is the wrong way to build
 * it: it does not exist on a phone, it does not exist for a keyboard, and a client reading a deal on an
 * iPad would never find it. So the affordance is a real button — it opens on click or tap, it opens on
 * hover and on focus for a pointer or keyboard user, Escape closes it, and the text is wired to the
 * button with `aria-describedby` so a screen reader gets it without any pointing at all.
 *
 * The copy is deliberately two beats: **what this is**, then **why it matters to you**. Canon's voice
 * is adult-to-adult and free of drama — the reader is a capable professional who wants to know what
 * they are looking at, not to be sold to inside their own deal.
 */

import { useEffect, useId, useRef, useState } from 'react';

export function HelpHint({ label, text }: { label: string; text: string }) {
  const [open, setOpen] = useState(false);
  const id = useId();
  const wrapRef = useRef<HTMLSpanElement>(null);

  // Escape closes, and a click elsewhere closes — the two things a reader tries when a popover is in
  // the way. Bound only while open, so the page carries no listeners it is not using.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onDown = (e: MouseEvent): void => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  return (
    <span
      className="hint"
      ref={wrapRef}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        className="hint__btn"
        aria-label={`What is ${label}?`}
        aria-expanded={open}
        aria-describedby={id}
        onClick={() => setOpen((v) => !v)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      >
        <span aria-hidden="true">i</span>
      </button>
      {/*
        Always rendered, hidden with `hidden` rather than unmounted: `aria-describedby` has to point at
        an element that exists, or a screen-reader user gets a button that describes nothing. This is
        the same reason the report's inactive sections stay in the DOM.
      */}
      <span role="tooltip" id={id} className="hint__bubble" hidden={!open}>
        {text}
      </span>
    </span>
  );
}
